/**
 * VideoFormDialog — crea/modifica un video della documentazione.
 *
 * Creazione: l'id documento viene GENERATO PRIMA dell'upload (newVideoDocId)
 * così il file MP4 sale su Storage al percorso canonico
 * `platform_docs_videos/{video_id}/{file}` con uploadBytesResumable e barra
 * di progresso; solo a upload riuscito si scrive il documento metadati.
 * Modifica: metadati sempre editabili, sostituzione file facoltativa.
 *
 * Durata: auto-rilevata dal file con un <video> off-DOM (campo vuoto se il
 * browser non legge i metadati — mai un valore inventato).
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, LinearProgress, MenuItem, Stack,
  TextField, Typography,
} from '@mui/material';
import { UploadFile, Save as SaveIcon } from '@mui/icons-material';
import type { UploadTask } from 'firebase/storage';
import { tokens } from '../../theme';
import { SoftChip } from '../SoftChip';
import {
  createVideo,
  detectVideoDurationSeconds,
  newVideoDocId,
  suggestedVersioneApp,
  updateVideo,
  uploadVideoFile,
} from '../../services/videoDocsService';
import type {
  PlatformDocsChapter,
  PlatformDocsVideo,
  VideoDocStato,
} from '../../types/videoDocs';
import {
  STATO_VIDEO_HINTS,
  STATO_VIDEO_LABELS,
  STATO_VIDEO_ORDER,
  STATO_VIDEO_TONES,
  formatDurataVideo,
} from './labels';

interface Props {
  open: boolean;
  /** Video da modificare; null = creazione nuova (upload obbligatorio). */
  video: PlatformDocsVideo | null;
  chapters: PlatformDocsChapter[];
  /** Tutti i video (per suggerire l'ordine successivo nel capitolo scelto). */
  videos: PlatformDocsVideo[];
  /** Suggerimenti aree coperte = union dei valori già presenti (zero hardcode). */
  areeSuggestions: string[];
  onClose: () => void;
  /** Chiamata dopo il salvataggio riuscito (il padre ricarica la lista). */
  onSaved: () => void;
}

/** Ordine successivo nel capitolo: max(ordine) + 1 (0 se il capitolo è vuoto). */
function nextOrdineInChapter(videos: PlatformDocsVideo[], chapterId: string): number {
  const inChapter = videos.filter((v) => v.chapter_id === chapterId);
  if (inChapter.length === 0) return 0;
  return Math.max(...inChapter.map((v) => v.ordine ?? 0)) + 1;
}

export const VideoFormDialog: React.FC<Props> = ({
  open, video, chapters, videos, areeSuggestions, onClose, onSaved,
}) => {
  const isEdit = video !== null;

  // Metadati
  const [titolo, setTitolo] = useState('');
  const [descrizione, setDescrizione] = useState('');
  const [chapterId, setChapterId] = useState('');
  const [ordine, setOrdine] = useState(0);
  const [ordineTouched, setOrdineTouched] = useState(false);
  const [versioneApp, setVersioneApp] = useState('');
  const [stato, setStato] = useState<VideoDocStato>('attuale');
  const [aree, setAree] = useState<string[]>([]);

  // File + durata rilevata
  const [file, setFile] = useState<File | null>(null);
  const [durataSecondi, setDurataSecondi] = useState<number | null>(null);
  const [detecting, setDetecting] = useState(false);

  // Salvataggio / upload
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const uploadTaskRef = useRef<UploadTask | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Inizializzazione all'apertura (nuovo o modifica)
  useEffect(() => {
    if (!open) return;
    setError(null);
    setFile(null);
    setProgress(null);
    setSaving(false);
    setDetecting(false);
    if (video) {
      setTitolo(video.titolo);
      setDescrizione(video.descrizione ?? '');
      setChapterId(video.chapter_id);
      setOrdine(video.ordine ?? 0);
      setOrdineTouched(true);
      setVersioneApp(video.versione_app ?? '');
      setStato(video.stato);
      setAree(video.aree_coperte ?? []);
      setDurataSecondi(typeof video.durata_secondi === 'number' ? video.durata_secondi : null);
    } else {
      const firstChapter = chapters[0]?.id ?? '';
      setTitolo('');
      setDescrizione('');
      setChapterId(firstChapter);
      setOrdine(firstChapter ? nextOrdineInChapter(videos, firstChapter) : 0);
      setOrdineTouched(false);
      setVersioneApp(suggestedVersioneApp());
      setStato('attuale');
      setAree([]);
      setDurataSecondi(null);
    }
  }, [open, video, chapters, videos]);

  const handleChapterChange = (newChapterId: string) => {
    setChapterId(newChapterId);
    // In creazione, finché l'operatore non tocca l'ordine, suggerisci la coda del capitolo
    if (!isEdit && !ordineTouched) {
      setOrdine(nextOrdineInChapter(videos, newChapterId));
    }
  };

  const handleFileSelected = async (selected: File | null) => {
    setError(null);
    if (!selected) {
      setFile(null);
      if (!isEdit) setDurataSecondi(null);
      return;
    }
    if (!/\.mp4$/i.test(selected.name)) {
      setError('Il file deve essere un video MP4 (.mp4)');
      return;
    }
    setFile(selected);
    setDetecting(true);
    const seconds = await detectVideoDurationSeconds(selected);
    setDetecting(false);
    setDurataSecondi(seconds);
  };

  /** Upload con progresso; risolve col percorso Storage a upload completato. */
  const uploadWithProgress = (videoId: string, toUpload: File): Promise<string> => {
    const { task, storagePath } = uploadVideoFile(videoId, toUpload);
    uploadTaskRef.current = task;
    setProgress(0);
    return new Promise<string>((resolve, reject) => {
      task.on(
        'state_changed',
        (snap) => {
          setProgress(snap.totalBytes > 0 ? (snap.bytesTransferred / snap.totalBytes) * 100 : 0);
        },
        (err) => reject(err),
        () => resolve(storagePath),
      );
    }).finally(() => {
      uploadTaskRef.current = null;
    });
  };

  const handleSave = async () => {
    const titoloTrim = titolo.trim();
    if (!titoloTrim) { setError('Il titolo è obbligatorio'); return; }
    if (!chapterId) { setError('Seleziona un capitolo'); return; }
    const versioneTrim = versioneApp.trim();
    if (!versioneTrim) { setError('La versione app è obbligatoria (es. AAAA.MM)'); return; }
    // Catturato in locale: TypeScript non può restringere lo state nella closure async
    const fileToUpload = file;
    if (!isEdit && !fileToUpload) { setError('Seleziona il file MP4 da caricare'); return; }

    setSaving(true);
    setError(null);
    try {
      const areeClean = Array.from(new Set(aree.map((a) => a.trim()).filter(Boolean)));
      const metadata = {
        titolo: titoloTrim,
        descrizione: descrizione.trim() || undefined,
        chapter_id: chapterId,
        ordine,
        versione_app: versioneTrim,
        stato,
        aree_coperte: areeClean,
        durata_secondi: durataSecondi ?? undefined,
      };

      if (!isEdit && fileToUpload) {
        // Id generato PRIMA dell'upload → percorso Storage canonico per-video
        const videoId = newVideoDocId();
        const storagePath = await uploadWithProgress(videoId, fileToUpload);
        await createVideo(videoId, { ...metadata, storage_path: storagePath });
      } else if (isEdit && fileToUpload) {
        // Sostituzione file: upload nella stessa cartella del video, poi patch
        const storagePath = await uploadWithProgress(video.id, fileToUpload);
        await updateVideo(video.id, { ...metadata, storage_path: storagePath });
      } else if (isEdit) {
        await updateVideo(video.id, metadata);
      }
      onSaved();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore durante il salvataggio');
    } finally {
      setSaving(false);
      setProgress(null);
    }
  };

  const handleClose = () => {
    // Annulla un eventuale upload in corso prima di chiudere
    uploadTaskRef.current?.cancel();
    uploadTaskRef.current = null;
    onClose();
  };

  const chapterOptions = useMemo(
    () => chapters.map((c) => ({ id: c.id, label: c.attivo ? c.titolo : `${c.titolo} (disattivo)` })),
    [chapters],
  );

  return (
    <Dialog open={open} onClose={saving ? undefined : handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontFamily: tokens.font.display, fontWeight: 600 }}>
        {isEdit ? 'Modifica video' : 'Nuovo video'}
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Stack spacing={2}>
          {/* FILE MP4 */}
          <Box>
            <input
              ref={fileInputRef}
              type="file"
              accept="video/mp4,.mp4"
              hidden
              onChange={(e) => handleFileSelected(e.target.files?.[0] ?? null)}
            />
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Button
                variant="outlined"
                startIcon={<UploadFile />}
                disabled={saving}
                onClick={() => fileInputRef.current?.click()}
              >
                {isEdit ? 'Sostituisci file MP4' : 'Scegli file MP4'}
              </Button>
              <Box sx={{ minWidth: 0 }}>
                {file ? (
                  <>
                    <Typography variant="body2" noWrap sx={{ fontWeight: 600, color: tokens.color.graphite }}>
                      {file.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: tokens.color.mist, fontFamily: tokens.font.mono }}>
                      {(file.size / (1024 * 1024)).toFixed(1)} MB
                      {' · '}
                      {detecting
                        ? 'rilevo la durata…'
                        : durataSecondi !== null
                          ? `durata ${formatDurataVideo(durataSecondi)}`
                          : 'durata non rilevata'}
                    </Typography>
                  </>
                ) : (
                  <Typography variant="caption" sx={{ color: tokens.color.mist }}>
                    {isEdit
                      ? 'Nessun nuovo file: resta il video già caricato.'
                      : 'Registrazione schermo in MP4 (1080p).'}
                  </Typography>
                )}
              </Box>
            </Stack>
            {progress !== null && (
              <Box sx={{ mt: 1.5 }}>
                <LinearProgress variant="determinate" value={progress} sx={{ borderRadius: 2, height: 6 }} />
                <Typography variant="caption" sx={{ color: tokens.color.ash, fontFamily: tokens.font.mono }}>
                  Upload {Math.round(progress)}%
                </Typography>
              </Box>
            )}
          </Box>

          {/* METADATI */}
          <TextField
            label="Titolo"
            value={titolo}
            required
            disabled={saving}
            onChange={(e) => setTitolo(e.target.value)}
            fullWidth
          />
          <TextField
            label="Descrizione (facoltativa)"
            value={descrizione}
            disabled={saving}
            onChange={(e) => setDescrizione(e.target.value)}
            multiline
            minRows={2}
            fullWidth
          />
          <Stack direction="row" spacing={1.5}>
            <TextField
              select
              label="Capitolo"
              value={chapterId}
              required
              disabled={saving}
              onChange={(e) => handleChapterChange(e.target.value)}
              sx={{ flex: 1 }}
            >
              {chapterOptions.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.label}</MenuItem>
              ))}
              {chapterOptions.length === 0 && (
                <MenuItem value="" disabled>Nessun capitolo: crealo prima</MenuItem>
              )}
            </TextField>
            <TextField
              label="Ordine"
              type="number"
              value={ordine}
              disabled={saving}
              onChange={(e) => {
                setOrdineTouched(true);
                const parsed = parseInt(e.target.value, 10);
                setOrdine(Number.isFinite(parsed) ? parsed : 0);
              }}
              inputProps={{ min: 0 }}
              sx={{ width: 110 }}
            />
          </Stack>
          <Stack direction="row" spacing={1.5}>
            <TextField
              label="Versione app"
              value={versioneApp}
              required
              disabled={saving}
              onChange={(e) => setVersioneApp(e.target.value)}
              helperText="Versione mostrata nel video (es. AAAA.MM)"
              sx={{ flex: 1 }}
            />
            <TextField
              select
              label="Stato"
              value={stato}
              disabled={saving}
              onChange={(e) => setStato(e.target.value as VideoDocStato)}
              helperText={STATO_VIDEO_HINTS[stato]}
              sx={{ flex: 1 }}
            >
              {STATO_VIDEO_ORDER.map((s) => (
                <MenuItem key={s} value={s}>
                  <SoftChip label={STATO_VIDEO_LABELS[s]} tone={STATO_VIDEO_TONES[s]} />
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          {/* AREE COPERTE — suggerimenti dai dati, mai hardcoded */}
          <Autocomplete
            multiple
            freeSolo
            options={areeSuggestions}
            value={aree}
            disabled={saving}
            onChange={(_e, value) => setAree(value)}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return <Chip key={key} size="small" label={option} {...tagProps} />;
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Aree coperte"
                placeholder="Id modulo/feature mostrati nel video"
                helperText="Registro per la manutenzione: quando un'area cambia, i video che la coprono vanno marcati da aggiornare. Invio per aggiungere."
              />
            )}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving && progress === null}>
          Annulla
        </Button>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
          disabled={saving || detecting}
          onClick={handleSave}
        >
          {isEdit ? 'Salva modifiche' : 'Carica e salva'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
